<script setup lang="ts">
import SparePartSelect from '@/components/rc/spare-parts/SparePartSelect.vue'
import { useRepairsContext } from '@/composables/rc/drawer-context'
import { useMinerSparePartReplacements } from '@/composables/rc/miner-spare-part-replacements'
import { usePartDetails } from '@/composables/rc/part-details'
import { Icon } from '@iconify/vue'
import { ref } from 'vue'

defineProps<{
  disabled?: boolean
}>()

const { minerId, selectedPart } = useRepairsContext()
const { customerId, partReplacement, partId, partType, movedToInventory } = usePartDetails(minerId, selectedPart)

const replacingPart = ref(false)

const selectedPartReplacement = ref(partReplacement.value)

const { usePartReplacementMutation } = useMinerSparePartReplacements(minerId)
const { isPending: replacePartMutationIsPending, mutate: replacePartMutation } = usePartReplacementMutation()

/**
 * Handle part replacement selection (send part replacement to server).
 *
 * @param spare_part selected replacement spare part.
 */
const handlePartReplacementChange = (spare_part: any) => {
  if (!partId.value) return
  replacePartMutation(
    {
      part_id: partId.value,
      part_type: partType.value,
      spare_part_id: spare_part.id,
    },
    {
      onSettled: () => {
        replacingPart.value = false
      },
      onError: () => {
        selectedPartReplacement.value = undefined
      },
    }
  )
}
</script>

<template>
  <div class="flex flex-col gap-4 w-full">
    <header class="inline-flex items-center justify-between">
      <el-text class="text-text-tertiary w-full">Replace part</el-text>

      <Icon v-if="replacePartMutationIsPending" icon="line-md:loading-loop" height="20" />
    </header>

    <!-- "Replace part" text button -->
    <el-tooltip content="Not available until part model is set" :disabled="!!partId">
      <el-link
        v-if="!replacingPart && !selectedPartReplacement"
        class="self-start flex gap-2"
        type="primary"
        :disabled="!partId || movedToInventory"
        @click="replacingPart = true"
        ><Icon icon="mdi:counterclockwise-arrows" :height="20" /> Replace part</el-link
      >
    </el-tooltip>

    <div v-if="replacingPart || selectedPartReplacement" class="flex flex-col gap-2">
      <div class="flex gap-xs">
        <SparePartSelect
          v-if="customerId && (selectedPartReplacement || replacingPart)"
          v-model="selectedPartReplacement"
          :disabled="!!selectedPartReplacement || replacePartMutationIsPending || disabled"
          :part-type="partType"
          :customer-id="customerId"
          @update:model-value="handlePartReplacementChange"
        />
        <Icon
          v-if="replacingPart"
          class="text-text-secondary hover:text-red cursor-pointer self-center"
          icon="material-symbols:close-rounded"
          :height="16"
          @click="replacingPart = false"
        />
      </div>
      <p v-if="selectedPartReplacement?.owner" class="text-xs text-text-placeholder">
        Owned by {{ selectedPartReplacement.owner }}
      </p>
    </div>
  </div>
</template>

<style lang="scss" scoped>
:deep() {
  .el-select-dropdown {
    &__header {
      border-bottom: none;
    }
  }

  .el-form-item {
    margin-bottom: 0;

    &__label {
      width: 100%;
    }
  }

  .el-button {
    height: 20px;
    width: 20px;
    padding: 0;
  }

  .el-link__inner {
    display: flex;
    gap: var(--spacing-spacing-2xs);
  }
}
</style>
